import { useState } from 'react';
import { IconCheck, IconEdit, IconPlus, IconTrash, IconUsers } from '../components/icons';
import { Badge, Card, EmptyState, ErrorMessage, Field, Loading, Modal, ModuleHeader, useAsync, useToast } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate } from '../lib/format';

export function CustomersPage() {
  const { can } = useAuth();
  const { notify } = useToast();
  const [editing, setEditing] = useState<any | null>(null);
  const customers = useAsync(() => api.get('/api/customers'), []);

  const mayManage = can('customers.manage');

  /**
   * Stilllegen oder endgültig löschen – wie bei den Objekten.
   * Ein Eigentümer/Verwalter trägt Verträge und SLA-Abweichungen; endgültig entfernen
   * lässt er sich nur, solange nichts mehr auf ihn zeigt.
   */
  const remove = async (customer: any, permanent: boolean) => {
    const question = permanent
      ? `Eigentümer/Verwalter „${customer.name}“ endgültig löschen? Nur möglich, solange keine Objekte oder Aufträge daran hängen.`
      : `Eigentümer/Verwalter „${customer.name}“ stilllegen? Verträge und Historie bleiben erhalten.`;
    if (!window.confirm(question)) return;

    try {
      await api.delete(`/api/customers/${customer.id}`, permanent ? { permanent: 'true' } : undefined);
      notify(permanent ? 'Eigentümer/Verwalter gelöscht.' : 'Eigentümer/Verwalter stillgelegt.', 'success');
      customers.reload();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Aktion nicht möglich.', 'error');
    }
  };

  const reactivate = async (customer: any) => {
    await api.post(`/api/customers/${customer.id}/reactivate`);
    notify('Eigentümer/Verwalter wieder aktiv.', 'success');
    customers.reload();
  };

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconUsers width={22} height={22} />}
        title="Eigentümer/Verwalter"
        subtitle="Kundenstamm mit Vertragsbezug – Grundlage für objektspezifische SLA und die Weiterberechnung."
        pill="Stammdaten"
        tone="info"
        actions={
          mayManage && (
            <button className="btn primary" onClick={() => setEditing({ isNew: true })}>
              <IconPlus width={15} height={15} /> Eigentümer/Verwalter anlegen
            </button>
          )
        }
      />

      <Card noBody>
        {customers.loading ? (
          <Loading />
        ) : !customers.data?.length ? (
          <EmptyState icon={<IconUsers width={20} height={20} />} title="Keine Eigentümer/Verwalter erfasst" />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Kundennummer</th>
                  <th>Ansprechpartner</th>
                  <th>Vertrag</th>
                  <th>Laufzeit ab</th>
                  <th className="num">Objekte</th>
                  {mayManage && <th className="actions">Aktionen</th>}
                </tr>
              </thead>
              <tbody>
                {customers.data.map((customer: any) => (
                  <tr key={customer.id} style={customer.is_active ? undefined : { opacity: 0.55 }}>
                    <td>
                      <strong>{customer.name}</strong>
                      {!customer.is_active && (
                        <span style={{ marginLeft: 8 }}>
                          <Badge>Stillgelegt</Badge>
                        </span>
                      )}
                      <div className="small subtle">{customer.address}</div>
                    </td>
                    <td className="mono small">{customer.customer_no ?? '–'}</td>
                    <td className="small">
                      {customer.contact_name ?? '–'}
                      <div className="subtle">{customer.contact_email}</div>
                    </td>
                    <td className="small mono">{customer.contract_ref ?? '–'}</td>
                    <td className="small">{customer.contract_start ? formatDate(customer.contract_start) : '–'}</td>
                    <td className="num">{customer.property_count}</td>
                    {mayManage && (
                      <td className="actions">
                        <button className="btn sm" onClick={() => setEditing(customer)}>
                          <IconEdit width={13} height={13} /> Bearbeiten
                        </button>
                        {customer.is_active ? (
                          <>
                            <button
                              className="btn sm ghost"
                              title="Stilllegen – Verträge und Historie bleiben erhalten"
                              style={{ marginLeft: 6 }}
                              onClick={() => remove(customer, false)}
                            >
                              Stilllegen
                            </button>
                            <button
                              className="btn sm ghost"
                              title="Endgültig löschen – nur ohne Verknüpfungen möglich"
                              onClick={() => remove(customer, true)}
                            >
                              <IconTrash width={13} height={13} />
                            </button>
                          </>
                        ) : (
                          <button className="btn sm ghost" style={{ marginLeft: 6 }} onClick={() => reactivate(customer)}>
                            <IconCheck width={13} height={13} /> Aktivieren
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <CustomerModal
          customer={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            customers.reload();
            notify(editing.isNew ? 'Eigentümer/Verwalter angelegt.' : 'Eigentümer/Verwalter gespeichert.', 'success');
          }}
        />
      )}
    </div>
  );
}

function CustomerModal({
  customer,
  onClose,
  onSaved,
}: {
  customer: any;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = Boolean(customer.isNew);
  const [form, setForm] = useState({
    name: customer.name ?? '',
    customerNo: customer.customer_no ?? '',
    contactName: customer.contact_name ?? '',
    contactEmail: customer.contact_email ?? '',
    contactPhone: customer.contact_phone ?? '',
    address: customer.address ?? '',
    contractRef: customer.contract_ref ?? '',
    contractStart: customer.contract_start?.slice(0, 10) ?? '',
    contractEnd: customer.contract_end?.slice(0, 10) ?? '',
  });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const set = (key: keyof typeof form, value: string) => setForm((current) => ({ ...current, [key]: value }));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (isNew) await api.post('/api/customers', form);
      else await api.patch(`/api/customers/${customer.id}`, form);
      onSaved();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={isNew ? 'Eigentümer/Verwalter anlegen' : `Eigentümer/Verwalter bearbeiten: ${customer.name}`}
      onClose={onClose}
      size="wide"
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn primary" disabled={busy || form.name.trim().length < 2} onClick={save}>
            Speichern
          </button>
        </>
      }
    >
      <div className="stack">
        <ErrorMessage error={error} />
        <div className="form-grid">
          <Field label="Name" required>
            <input value={form.name} onChange={(event) => set('name', event.target.value)} autoFocus />
          </Field>
          <Field label="Kundennummer">
            <input value={form.customerNo} onChange={(event) => set('customerNo', event.target.value)} />
          </Field>
          <Field label="Ansprechpartner">
            <input value={form.contactName} onChange={(event) => set('contactName', event.target.value)} />
          </Field>
          <Field label="E-Mail">
            <input type="email" value={form.contactEmail} onChange={(event) => set('contactEmail', event.target.value)} />
          </Field>
          <Field label="Telefon">
            <input value={form.contactPhone} onChange={(event) => set('contactPhone', event.target.value)} />
          </Field>
          <Field label="Vertragsreferenz">
            <input value={form.contractRef} onChange={(event) => set('contractRef', event.target.value)} />
          </Field>
          <Field label="Vertragsbeginn">
            <input type="date" value={form.contractStart} onChange={(event) => set('contractStart', event.target.value)} />
          </Field>
          <Field label="Vertragsende">
            <input type="date" value={form.contractEnd} onChange={(event) => set('contractEnd', event.target.value)} />
          </Field>
        </div>
        <Field label="Anschrift">
          <input value={form.address} onChange={(event) => set('address', event.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}
